import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TextField,
  Box,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
  Switch,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DynamicFeed } from '@mui/icons-material';
import { GlobeIcon, LockIcon, XIcon, UserPlusIcon } from '@phosphor-icons/react';
import { User } from 'firebase/auth';
import { useUserWatchlistData } from '../../contexts/UserWatchlistDataContext';
import { useToast } from '../utils/ToastProvider';
import { glassStyles } from '../../tokens/designTokens';
import CustomButton from '../widgets/CustomButton';
import CustomTabs from '../widgets/CustomTabs';
import CollaboratorPicker from './CollaboratorPicker';
import { CollaborationRequestService } from '../../services/collaborationRequestService';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';
import { font } from '../../utils/fontHelper';
import { useKeyboardAware } from '../../hooks/useKeyboardAware';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';


interface CreateListDialogProps {
  onBack: () => void;
  onListCreated: (listId: string) => void;
  user?: User;
  // Edit mode props
  editMode?: boolean;
  editWatchlistId?: string;
  initialName?: string;
  initialDescription?: string;
  initialIsPrivate?: boolean;
  initialCollaborators?: string[];
  // New props for field control
  disableNameEdit?: boolean;
  allowPrivacyEdit?: boolean;
  allowDescriptionEdit?: boolean;
  /**
   * solo — collaborators section hidden; "You can add collaborators later."
   * collaborative — collaborators section shown; save disabled until ≥ 1 added.
   * Default 'solo' preserves the legacy "collaborators optional" UX.
   */
  mode?: 'solo' | 'collaborative';
}

const CreateListDialog: React.FC<CreateListDialogProps> = ({
  onBack,
  onListCreated,
  user,
  editMode = false,
  editWatchlistId,
  initialName = '',
  initialDescription = '',
  initialIsPrivate = false,
  initialCollaborators = [],
  disableNameEdit = false,
  allowPrivacyEdit = true,
  allowDescriptionEdit = true,
  mode = 'solo',
}) => {
  const [listName, setListName] = useState(initialName);
  const [listDescription, setListDescription] = useState(initialDescription);
  const [hasUserStartedEditing, setHasUserStartedEditing] = useState(false);

  // Privacy settings - use simple boolean for single selection
  const [isPrivate, setIsPrivate] = useState(initialIsPrivate);
  const [shareToFeed, setShareToFeed] = useState(!initialIsPrivate);

  // Collaborator state
  const [showCollaboratorPicker, setShowCollaboratorPicker] = useState(false);
  const [selectedCollaborators, setSelectedCollaborators] = useState<string[]>(initialCollaborators || []);

  const textFieldRef = useRef<HTMLInputElement>(null);
  const [isSaving, setIsSaving] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isKeyboardOpen, bottomPadding, scrollIntoViewOnFocus } = useKeyboardAware();

  const { getUserDisplayName, getUserAvatar } = useUserProfileCache();

  // Use UserWatchlistData context
  const { createWatchlist, updateWatchlist } = useUserWatchlistData();
  const { showError, showSuccess, showWarning } = useToast();


  const handleRemoveSelectedCollaborator = useCallback((uid: string) => {
    setSelectedCollaborators(prev => prev.filter(id => id !== uid));
  }, []);

  const renderSelectedCollaboratorsPreview = useCallback(() => {
    if (selectedCollaborators.length === 0) {
      return null;
    }

    return (
      <Box
        sx={{
          mt: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          rowGap: 1.5,
        }}
      >
        {selectedCollaborators.map((uid) => {
          const name = getUserDisplayName(uid) || 'Collaborator';
          const avatarUrl = getUserAvatar(uid);
          const fallbackInitial = name.charAt(0).toUpperCase();

          return (
            <Box
              key={uid}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 999,
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(125, 122, 255, 0.15)'
                  : 'rgba(125, 122, 255, 0.12)',
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(125, 122, 255, 0.4)' : 'rgba(125, 122, 255, 0.3)'}`,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 16px rgba(0, 0, 0, 0.45)'
                  : '0 4px 16px rgba(60, 59, 110, 0.12)',
                maxWidth: '100%',
              }}
            >
              <Avatar
                src={avatarUrl || undefined}
                alt={name}
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: 13,
                  bgcolor: avatarUrl ? 'transparent' : theme.palette.primary.main,
                  color: avatarUrl ? undefined : theme.palette.getContrastText(theme.palette.primary.main),
                }}
              >
                {!avatarUrl && fallbackInitial}
              </Avatar>
              <Tooltip title={name} arrow>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: font(),
                    fontWeight: 500,
                    fontSize: 13,
                    maxWidth: 140,
                    color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {name}
                </Typography>
              </Tooltip>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemoveSelectedCollaborator(uid);
                }}
                sx={{
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8)' : theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <XIcon size={16} />
              </IconButton>
            </Box>
          );
        })}
      </Box>
    );
  }, [selectedCollaborators, getUserDisplayName, getUserAvatar, theme, handleRemoveSelectedCollaborator]);

  // Debug: Basic component logging
  useEffect(() => {
    console.log('CreateListDialog: Component ready');

    // For new lists (not edit mode), set hasUserStartedEditing to true after initial setup
    if (!editMode) {
      setTimeout(() => {
        setHasUserStartedEditing(true);
      }, 100);
    }
  }, [editMode]);

  // Update state when initial props change (for edit mode) - but only if user hasn't started editing
  useEffect(() => {
    if (!hasUserStartedEditing) {
      setListName(initialName);
      setListDescription(initialDescription);
      setIsPrivate(initialIsPrivate);
    }
  }, [initialName, initialDescription, initialIsPrivate, hasUserStartedEditing]);

  useEffect(() => {
    // Auto-focus and open keyboard
    if (textFieldRef.current) {
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 100);
    }
  }, []);




  // Handle dialog close
  const handleClose = useCallback(() => {
    onBack();
  }, [onBack]);

  const handleSave = async () => {
    if (!listName.trim()) return;

    // Basic validation for empty names
    if (!listName.trim()) {
      showError('Watchlist name cannot be empty');
      return;
    }

    setIsSaving(true);
    try {
      if (editMode && editWatchlistId) {
        // Update existing watchlist
        const updateData: any = {
          name: listName.trim(),
          isPrivate: isPrivate,
        };

        // Only add optional fields if they have actual values
        if (listDescription.trim()) {
          updateData.description = listDescription.trim();
        } else {
          updateData.description = ''; // Clear description if empty
        }

        await updateWatchlist(editWatchlistId, updateData);
        showSuccess('Watchlist updated successfully!');
        onListCreated(editWatchlistId);
      } else {
        // Create new watchlist
        const watchlistData: any = {
          name: listName.trim(),
          movieDetails: [],
          isPrivate: isPrivate,
          createdBy: user?.uid || 'anonymous',
          createdByName: user?.displayName || 'You',
          tags: []
        };

        // Only add optional fields if they have actual values
        if (listDescription.trim()) {
          watchlistData.description = listDescription.trim();
        }

        const watchlistId = await createWatchlist(watchlistData, shareToFeed);

        // Send collaboration requests for selected collaborators
        if (selectedCollaborators.length > 0 && user?.uid) {
          console.log('CreateListDialog: Sending collaboration requests:', {
            watchlistId,
            isPrivate,
            fromUserId: user.uid,
            toUserIds: selectedCollaborators,
            watchlistName: listName.trim()
          });

          const inviteResults = await Promise.allSettled(
            selectedCollaborators.map(collaboratorId =>
              CollaborationRequestService.sendCollaborationRequest(
                watchlistId,
                user.uid,
                collaboratorId,
                'editor'
              )
            )
          );

          // Log detailed results
          inviteResults.forEach((result, index) => {
            if (result.status === 'fulfilled') {
              console.log(`✅ Collaboration request sent to ${selectedCollaborators[index]}: ${result.value}`);
            } else {
              console.error(`❌ Failed to send collaboration request to ${selectedCollaborators[index]}:`, result.reason);
            }
          });

          const successCount = inviteResults.filter(r => r.status === 'fulfilled').length;
          const failCount = inviteResults.filter(r => r.status === 'rejected').length;

          if (successCount > 0 && failCount === 0) {
            showSuccess(`Watchlist created and ${successCount} collaboration ${successCount === 1 ? 'invite' : 'invites'} sent!`);
          } else if (successCount > 0 && failCount > 0) {
            showWarning(`Watchlist created! ${successCount} ${successCount === 1 ? 'invite' : 'invites'} sent, but ${failCount} failed.`);
          } else if (failCount > 0) {
            showError('Watchlist created, but collaboration invites failed. Check console for details.');
          }
        } else {
          showSuccess('Watchlist created successfully!');
        }

        onListCreated(watchlistId);
      }

    } catch (error) {
      console.error('CreateListDialog: Error saving watchlist:', error);
      showError(editMode ? 'Failed to update watchlist. Please try again.' : 'Failed to create watchlist. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const dialogContent = (
    <>
      <BottomSheetDraggableHeader sx={{
        display: 'flex',
        alignItems: 'center',
        pb: isMobile ? 1 : 2,
        pt: 2.5,
        px: 3,
        fontFamily: font(),
        fontWeight: 600,
        fontSize: 18,
      }}>
        {editMode ? 'Edit Watchlist' : 'Create New Watchlist'}
      </BottomSheetDraggableHeader>

      <IconButton
        onClick={handleClose}
        size="small"
        sx={{
          position: 'absolute',
          top: 18,
          right: 16,
          color: theme.palette.text.primary,
          zIndex: 1,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
          '&:hover': {
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          }
        }}
      >
        <XIcon size={20} />
      </IconButton>

      <DialogContent sx={{
        px: 3,
        py: 0,
        flex: 1,
        minHeight: 0, // Allow flex shrinking
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
        pb: isKeyboardOpen ? `${bottomPadding}px` : 0,
        transition: 'padding-bottom 0.25s ease-out',
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
          {/* List Name Input */}
          {!disableNameEdit && (
            <Box>
              <Typography variant="subtitle2" sx={{
                mb: 1.5,
                fontWeight: 600,
                fontFamily: font(),
                color: theme.palette.text.secondary
              }}>
                List Name
              </Typography>
              <TextField
                fullWidth
                value={listName}
                onChange={(e) => {
                  if (e.target.value.length <= 70) {
                    setListName(e.target.value);
                    setHasUserStartedEditing(true);
                  }
                }}
                onFocus={scrollIntoViewOnFocus}
                placeholder="e.g., Movies to Watch This Summer"
                variant="outlined"
                inputRef={textFieldRef}
                disabled={disableNameEdit}
                helperText={listName.length >= 70 ? `${listName.length}/70` : undefined}
                inputProps={{ 'data-testid': 'watchlist-name-input', style: { fontSize: 16 }, maxLength: 70 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: '2px'
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    py: 1.5,
                    fontFamily: font(),
                  }
                }}
              />
            </Box>
          )}

          {/* List Description Input */}
          {allowDescriptionEdit && (
            <Box>
              <Typography variant="subtitle2" sx={{
                mb: 1.5,
                fontWeight: 600,
                fontFamily: font(),
                color: theme.palette.text.secondary
              }}>
                Description (optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={listDescription}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setListDescription(e.target.value);
                    setHasUserStartedEditing(true);
                  }
                }}
                onFocus={scrollIntoViewOnFocus}
                placeholder="Tell others what this list is about..."
                variant="outlined"
                disabled={!allowDescriptionEdit}
                helperText={`${listDescription.length}/500`}
                inputProps={{ 'data-testid': 'watchlist-description-input', style: { fontSize: 16 }, maxLength: 500 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {

                    fontFamily: font(),
                  }
                }}
              />
            </Box>
          )}

          {/* Privacy Section - CustomTabs */}
          {allowPrivacyEdit && (
            <Box>
              <Typography variant="subtitle2" sx={{
                mb: 1.5,
                fontWeight: 600,
                fontFamily: font(),
                color: theme.palette.text.secondary
              }}>
                Privacy
              </Typography>
              <CustomTabs
                value={isPrivate ? 1 : 0}
                onChange={(newValue) => {
                  const nowPrivate = newValue === 1;
                  setIsPrivate(nowPrivate);
                  if (nowPrivate) setShareToFeed(false);
                  setHasUserStartedEditing(true);
                }}
                tabs={[
                  { label: 'Public', value: 0, icon: <GlobeIcon /> },
                  { label: 'Private', value: 1, icon: <LockIcon /> }
                ]}
              />
            </Box>
          )}

          {/* Share to Activity Feed Toggle — create mode, public lists only */}
          {!editMode && !isPrivate && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.75,
                py: 1.25,
                borderRadius: 2.5,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.4)',
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <DynamicFeed sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                <Box>
                  <Typography sx={{ fontFamily: font(), fontSize: 14, color: theme.palette.text.primary, fontWeight: 500 }}>
                    Post on activity feed
                  </Typography>
                  <Typography sx={{ fontFamily: font(), fontSize: 11, color: theme.palette.text.secondary }}>
                    {shareToFeed ? 'Let your crew know about this list' : 'Hidden from crew feed'}
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={shareToFeed}
                onChange={(e) => setShareToFeed(e.target.checked)}
                size="small"
                color="primary"
              />
            </Box>
          )}

          {/* Collaborators Section */}
          {!editMode && mode === 'collaborative' && (
            <Box>
              <Typography variant="subtitle2" sx={{
                mb: 1.5,
                fontWeight: 600,
                fontFamily: font(),
                color: theme.palette.text.secondary
              }}>
                Collaborators
              </Typography>
              <CustomButton
                onClick={() => setShowCollaboratorPicker(true)}
                disabled={!listName.trim()}
                semantic="tertiary"
                icon={<UserPlusIcon />}
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  fontWeight: 500,
                  fontSize: 14,
                  textTransform: 'none',
                  py: 1.5,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                }}
              >
                {selectedCollaborators.length > 0
                  ? `Invite collaborators (${selectedCollaborators.length})`
                  : 'Invite collaborators'}
              </CustomButton>
              {!listName.trim() && (
                <Typography sx={{
                  fontFamily: font(),
                  fontSize: 12,
                  color: theme.palette.text.secondary,
                  mt: 0.5,
                  ml: 1,
                }}>
                  Enter a list name first
                </Typography>
              )}
              {renderSelectedCollaboratorsPreview()}
            </Box>
          )}

          {!editMode && mode === 'solo' && (
            <Typography
              sx={{
                fontFamily: font(),
                fontSize: 12,
                color: theme.palette.text.secondary,
                mt: -0.5,
              }}
            >
              You can add collaborators later.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{
        p: 3,
        pt: 1,
        pb: isMobile ? 'calc(env(safe-area-inset-bottom, 24px) + 16px)' : 3
      }}>
        <CustomButton
          data-testid="save-watchlist-button"
          onClick={handleSave}
          disabled={
            !listName.trim() ||
            isSaving ||
            (!editMode && mode === 'collaborative' && selectedCollaborators.length === 0)
          }
          variant="contained"
          semantic="brand"
          sx={{
            width: '100%',
            py: 1.5,
            fontSize: 16,
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: font(),
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.4)'
              : '0 8px 32px rgba(125, 122, 255, 0.2)',
          }}
        >
          {isSaving ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Save Changes' : 'Save')}
        </CustomButton>
      </DialogActions>
    </>
  );

  return (
    <>
      {isMobile ? (
        <BottomSheet open={true} onClose={handleClose} maxHeight="90dvh">
          {dialogContent}
        </BottomSheet>
      ) : (
        <Dialog
          open={true}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              ...glassStyles.popup(theme.palette.mode === 'dark'),
            }
          }}
        >
          {showCollaboratorPicker ? (
            <CollaboratorPicker
              open={showCollaboratorPicker}
              onClose={() => setShowCollaboratorPicker(false)}
              listName={listName}
              selectedCollaborators={selectedCollaborators}
              onCollaboratorsChange={setSelectedCollaborators}
              user={user}
            />
          ) : (
            dialogContent
          )}
        </Dialog>
      )}

      {/* CollaboratorPicker for Mobile - Separate Bottom Sheet */}
      {isMobile && (
        <CollaboratorPicker
          open={showCollaboratorPicker}
          onClose={() => setShowCollaboratorPicker(false)}
          listName={listName}
          selectedCollaborators={selectedCollaborators}
          onCollaboratorsChange={setSelectedCollaborators}
          user={user}
        />
      )}
    </>
  );
};

export default CreateListDialog;
